/**
 * API client — wraps fetch with:
 * - Auto Bearer token from localStorage
 * - JSON body serialization
 * - 401 → attempt refresh → retry once
 * - Consistent error shape: { error: { code, message, details } }
 *
 * W1 Arabic (frontend lang wiring):
 * - Student content endpoints append ?lang= from localStorage.drfahm_lang
 *   (the frontend-only preference i18n.js maintains). Read at CALL time, not
 *   module load, so toggling language mid-session takes effect immediately.
 * - Absent/unknown value → no param → backend defaults to 'en' (M3a contract),
 *   so old sessions behave byte-identically.
 */

const BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000';

function getTokens() {
  return {
    access: localStorage.getItem('access_token'),
    refresh: localStorage.getItem('refresh_token'),
  };
}

function setTokens({ access_token, refresh_token }) {
  if (access_token) localStorage.setItem('access_token', access_token);
  if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
}

function clearTokens() {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
}

// ── W1 Arabic: language query helper ────────────────────────────────────────
// Only ever emits validated values ('en'|'ar'); anything else → empty string,
// which the backend treats as absent → 'en'. Never throws.
function langValue() {
  let lang = null;
  try {
    lang = localStorage.getItem('drfahm_lang');
  } catch {
    return null;
  }
  return lang === 'ar' || lang === 'en' ? lang : null;
}

// ── Chunk 27: TEMPORARY content-language pin ────────────────────────────────
// The AR question bank is not yet populated, but the language toggle drives
// BOTH the UI translation and the content track (M3a). Without this pin, a
// student flipping the navbar to العربية gets Arabic menus and EMPTY levels
// (the backend scopes strictly by language with no EN fallback).
// Pin scope: CONTENT endpoints only (questions/submit/weak-topics). The UI
// translation stays fully free, and schools.dashboard still sends the real
// language (the teacher dashboard's Arabic is report language, not content).
// UN-PIN when Arabic worlds are filled to 100% and activated: set
// CONTENT_LANG_PIN = null below — one line, frontend-only deploy.
const CONTENT_LANG_PIN = 'en';

function contentLangValue() {
  return CONTENT_LANG_PIN || langValue();
}

function langQuery() {
  const lang = contentLangValue();
  return lang ? `?lang=${lang}` : '';
}

async function refreshAccessToken() {
  const { refresh } = getTokens();
  if (!refresh) throw new Error('No refresh token');

  const res = await fetch(`${BASE}/api/auth/refresh`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${refresh}` },
  });
  if (!res.ok) throw new Error('Refresh failed');
  const data = await res.json();
  setTokens({ access_token: data.access_token });
  return data.access_token;
}

async function request(path, options = {}, retry = true) {
  const { access } = getTokens();
  const headers = { 'Content-Type': 'application/json', ...options.headers };
  if (access) headers['Authorization'] = `Bearer ${access}`;

  const res = await fetch(`${BASE}${path}`, { ...options, headers });

  // Token expired — try refresh once
  if (res.status === 401 && retry) {
    try {
      await refreshAccessToken();
      return request(path, options, false);
    } catch {
      clearTokens();
      window.dispatchEvent(new Event('auth:logout'));
      throw { status: 401, error: { code: 'unauthenticated', message: 'Session expired.' } };
    }
  }

  // Parse JSON regardless of status
  let body;
  try {
    body = await res.json();
  } catch {
    body = {};
  }

  if (!res.ok) {
    throw { status: res.status, ...body };
  }

  return body;
}

// ── Auth ────────────────────────────────────────────────────────────────────

export const auth = {
  register: (data) => request('/api/auth/register', { method: 'POST', body: JSON.stringify(data) }),
  login:    (data) => request('/api/auth/login',    { method: 'POST', body: JSON.stringify(data) }),
  refresh:  ()     => request('/api/auth/refresh',  { method: 'POST' }),
  me:       ()     => request('/api/auth/me'),
  logout:   ()     => request('/api/auth/logout',   { method: 'POST' }),
  // Chunk 44 (N6): the ONE language the student sits Qudurat in ('en'|'ar').
  // Decides which VERBAL bank their Qudurat verbal levels serve — set on the
  // backend, so CONTENT_LANG_PIN above has no effect on verbal. Returns { user }.
  setQuduratLang: (lang) => request('/api/auth/me/qudurat-lang', { method: 'PATCH', body: JSON.stringify({ lang }) }),
};

// ── Billing ─────────────────────────────────────────────────────────────────

export const billing = {
  createCheckoutSession: (data) =>
    request('/api/billing/create-checkout-session', { method: 'POST', body: JSON.stringify(data) }),
  getEntitlements: () => request('/api/billing/entitlements'),
};

// ── Exams ───────────────────────────────────────────────────────────────────

export const exams = {
  worldMap:   (exam) => request(`/api/exams/${exam}/world-map`),
  progress:   (exam) => request(`/api/exams/${exam}/progress`),

  // M1: leaderboard for an exam (top 20 + current user rank)
  // D3: optional { scope: 'global'|'school', period: 'all'|'week', orgId }
  // No options => byte-identical to the pre-D3 call (global, all-time).
  // orgId is admin-only (backend enforces); leaders get their own org.
  leaderboard: (exam, { scope, period, orgId } = {}) => {
    const params = new URLSearchParams();
    if (scope)  params.set('scope',  scope);
    if (period) params.set('period', period);
    if (orgId)  params.set('org_id', orgId);
    const qs = params.toString();
    return request(`/api/exams/${exam}/leaderboard${qs ? `?${qs}` : ''}`);
  },

  // M3: weak topics for the current user.
  // Returns: { weak_topics, based_on_levels, section }
  // W1 Arabic: lang appended so weak-topic analysis follows the content
  // language the student is practicing in.
  // Chunk 25: optional `section` (math/verbal/biology/chemistry/physics)
  // scopes topics AND based_on_levels to one section — used by the Exam page
  // so the panel matches the active section tab. Omitted => exam-wide
  // (byte-identical to the pre-Chunk-25 call; Dashboard overview keeps this).
  weakTopics: (exam, section) => {
    const params = new URLSearchParams();
    const lang = contentLangValue();
    if (lang) params.set('lang', lang);
    if (section) params.set('section', section);
    const qs = params.toString();
    return request(`/api/exams/${exam}/weak-topics${qs ? `?${qs}` : ''}`);
  },

  // M2: predicted exam score for the current user.
  // Returns: { score, sections, based_on_levels, confidence }
  // Note: also injected into the submit_level response body — use that on the
  // results screen to avoid an extra round-trip.
  predictedScore: (exam) => request(`/api/exams/${exam}/predicted-score`),

  // Canonical names (used by Level.jsx)
  // W1 Arabic (M3a contract): ?lang= on questions AND submit — the grading
  // query is language-scoped too, so BOTH must carry the same lang or an AR
  // student would be graded against the EN question at the same index.
  getQuestions: (exam, worldKey, levelNumber) =>
    request(`/api/exams/${exam}/worlds/${worldKey}/levels/${levelNumber}/questions${langQuery()}`),

  // M1: durationSeconds (optional int) — elapsed seconds for this attempt.
  // Backend stores it on the first passing attempt only (leaderboard tiebreaker).
  submitLevel: (exam, worldKey, levelNumber, answers, durationSeconds) =>
    request(`/api/exams/${exam}/worlds/${worldKey}/levels/${levelNumber}/submit${langQuery()}`, {
      method: 'POST',
      body: JSON.stringify({
        answers,
        ...(durationSeconds != null ? { duration_seconds: durationSeconds } : {}),
      }),
    }),

  // Legacy aliases — kept so any existing callers don't break
  questions: (exam, worldKey, levelNumber) =>
    request(`/api/exams/${exam}/worlds/${worldKey}/levels/${levelNumber}/questions${langQuery()}`),

  submit: (exam, worldKey, levelNumber, answers, durationSeconds) =>
    request(`/api/exams/${exam}/worlds/${worldKey}/levels/${levelNumber}/submit${langQuery()}`, {
      method: 'POST',
      body: JSON.stringify({
        answers,
        ...(durationSeconds != null ? { duration_seconds: durationSeconds } : {}),
      }),
    }),
};

// ── Schools (teacher dashboard, D2/D4) ───────────────────────────────────────

export const schools = {
  // GET /api/schools/:orgId/dashboard?exam=&class_group=&lang=
  // Backend enforces org ownership for school leaders; admins may pass any org.
  // Returns the shape documented in backend/app/utils/teacher_stats.py.
  dashboard: (orgId, { exam, classGroup } = {}) => {
    const params = new URLSearchParams();
    if (exam) params.set('exam', exam);
    if (classGroup) params.set('class_group', classGroup);
    const lang = langValue();
    if (lang) params.set('lang', lang);
    const qs = params.toString();
    return request(`/api/schools/${orgId}/dashboard${qs ? `?${qs}` : ''}`);
  },
};

// ── Mock exams (Chunk 51; backend Chunk 50) ──────────────────────────────────
// Teacher calls: leaders act on their own org; admins pass orgId.
// Student calls: mine / start / attempt / answer / next.

export const mocks = {
  listAssignments: (orgId) =>
    request(`/api/mocks/assignments${orgId ? `?org_id=${orgId}` : ''}`),
  createAssignment: (data) =>
    request('/api/mocks/assignments', { method: 'POST', body: JSON.stringify(data) }),
  deleteAssignment: (id) =>
    request(`/api/mocks/assignments/${id}`, { method: 'DELETE' }),
  results: (id) => request(`/api/mocks/assignments/${id}/results`),

  mine:    ()   => request('/api/mocks/mine'),
  start:   (id) => request(`/api/mocks/assignments/${id}/start`, { method: 'POST' }),
  attempt: (id) => request(`/api/mocks/attempts/${id}`),
  answer:  (attemptId, questionId, answer) =>
    request(`/api/mocks/attempts/${attemptId}/answer`, {
      method: 'POST', body: JSON.stringify({ question_id: questionId, answer }),
    }),
  next:    (attemptId) => request(`/api/mocks/attempts/${attemptId}/next`, { method: 'POST' }),
};

export { setTokens, clearTokens, getTokens };